import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { servicesData } from '../data/servicesData';
import PremiumButton from '../components/ui/PremiumButton';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

export default function ServiceDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = servicesData.find(s => s.id === id);
  const currentIndex = servicesData.findIndex(s => s.id === id);
  const nextService = servicesData[(currentIndex + 1) % servicesData.length];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 px-4 text-center">
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <button onClick={() => navigate('/')} className="text-cyan-500 hover:text-cyan-400 flex items-center gap-2">
          <FaArrowLeft /> Back to Home
        </button>
      </div>
    );
  }

  const IconComponent = service.icon;
  const NextIcon = nextService.icon;

  return (
    <div className="pt-28 pb-24 px-4 min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/3 to-transparent pointer-events-none" />
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r ${service.gradient} opacity-5 blur-3xl rounded-full pointer-events-none`} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Back button */}
        <motion.div {...fadeUp(0)} className="mb-10">
          <Link to="/#services" className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white transition-colors bg-gray-50/50 dark:bg-gray-900/50 px-5 py-2.5 rounded-full border border-gray-200 dark:border-gray-800 backdrop-blur-sm font-medium">
            <FaArrowLeft /> Back to Services
          </Link>
        </motion.div>

        {/* Hero Header */}
        <motion.div {...fadeUp(0.1)} className="relative bg-gradient-to-br from-gray-50/90 dark:from-gray-900/90 to-white/90 dark:to-black/90 p-8 md:p-14 rounded-3xl border border-gray-200 dark:border-gray-800 backdrop-blur-md overflow-hidden shadow-2xl mb-8">
          <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${service.gradient}`} />
          <div className="absolute top-6 right-6 opacity-5">
            <IconComponent className="text-[200px]" />
          </div>

          <div className="flex flex-col sm:flex-row gap-8 items-start">
            <div className={`w-28 h-28 rounded-2xl flex-shrink-0 bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-2xl relative overflow-hidden`}>
              <IconComponent className="text-5xl text-gray-900 dark:text-white relative z-10" />
              <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 3, repeat: Infinity }} className="absolute inset-0 bg-white/20 rounded-2xl" />
            </div>
            <div>
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-gray-500 mb-3">Premium Service</span>
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-black mb-4 bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent leading-tight`}>
                {service.title}
              </h1>
              <p className="text-2xl text-gray-700 dark:text-gray-300 font-semibold italic">{service.tagline}</p>
            </div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div {...fadeUp(0.2)} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {service.stats.map((stat, i) => (
            <div key={i} className="bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className={`text-3xl md:text-4xl font-black bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent mb-1`}>{stat.value}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Overview */}
        <motion.div {...fadeUp(0.25)} className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Overview</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">{service.details}</p>
          </div>
          <div className={`bg-gradient-to-br ${service.gradient} p-[1px] rounded-2xl`}>
            <div className="bg-gray-950 rounded-2xl p-8 h-full">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Why It Matters</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">{service.whyItMatters}</p>
            </div>
          </div>
        </motion.div>

        {/* What You Get */}
        <motion.div {...fadeUp(0.3)} className="bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What You Get</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.features.map((f, i) => (
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }}
                key={i} className="flex items-center gap-3 bg-white/5 px-5 py-4 rounded-xl border border-black/10 dark:border-white/10">
                <FaCheckCircle className={`text-xl flex-shrink-0 bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`} style={{ color: 'transparent', WebkitBackgroundClip: 'text', backgroundImage: `linear-gradient(to right, var(--tw-gradient-from, #22d3ee), var(--tw-gradient-to, #3b82f6))` }} />
                <span className="text-gray-200 font-medium">{f}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Our Process */}
        <motion.div {...fadeUp(0.35)} className="bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Our Process</h2>
          <div className="space-y-0">
            {service.process.map((step, i) => (
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
                key={i} className="flex gap-6 items-start relative">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white font-black text-sm shadow-lg`}>
                    {step.step}
                  </div>
                  {i < service.process.length - 1 && (
                    <div className="w-0.5 h-12 bg-gradient-to-b from-gray-600 to-transparent mt-1" />
                  )}
                </div>
                <div className="pb-10">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Platforms / Deliverables */}
        <motion.div {...fadeUp(0.4)} className="bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Platforms & Deliverables</h2>
          <div className="flex flex-wrap gap-3">
            {service.platforms.map((p, i) => (
              <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + i * 0.05 }}
                key={i} className={`px-5 py-2 rounded-full border text-sm font-semibold bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}
                style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                {p}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeUp(0.45)} className={`bg-gradient-to-r ${service.gradient} p-[1px] rounded-3xl mb-8`}>
          <div className="bg-gray-950 rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2">Ready to get started?</h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">Let's discuss how we can transform your business with {service.title}.</p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              <PremiumButton primary large onClick={() => navigate('/contact')}>Get Started Now</PremiumButton>
              <PremiumButton large onClick={() => navigate('/pricing')}>Request Pricing</PremiumButton>
            </div>
          </div>
        </motion.div>

        {/* Next Service */}
        <motion.div {...fadeUp(0.5)}>
          <p className="text-gray-500 text-sm font-medium mb-3 ml-1">Explore Next Service →</p>
          <Link to={`/services/${nextService.id}`}>
            <div className="group flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 hover:border-gray-600 rounded-2xl p-6 backdrop-blur-sm transition-all">
              <div className="flex items-center gap-5">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${nextService.gradient} flex items-center justify-center`}>
                  <NextIcon className="text-2xl text-gray-900 dark:text-white" />
                </div>
                <div>
                  <div className="text-gray-900 dark:text-white font-bold text-lg">{nextService.title}</div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">{nextService.tagline}</div>
                </div>
              </div>
              <FaArrowRight className="text-gray-500 group-hover:text-gray-900 dark:text-white group-hover:translate-x-2 transition-all text-xl" />
            </div>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
